import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    SquaresFour,
    ShoppingBag,
    Stack,
    Receipt,
    Notebook,
    GraduationCap,
    Users,
    Sparkle,
    SignOut,
    MagnifyingGlass,
    Bell,
    Gear,
    EnvelopeSimple,
    Phone,
    List,
    X,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { useEffect, useState } from "react";
import api from "../lib/api";

const NAV = [
    { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
    { to: "/orders", label: "Orders", icon: ShoppingBag },
    { to: "/services", label: "Services", icon: Stack },
    { to: "/invoices", label: "Invoices", icon: Receipt },
    { to: "/khatabook", label: "Khatabook", icon: Notebook },
    { to: "/students", label: "Coaching", icon: GraduationCap },
    { to: "/users", label: "Team", icon: Users, adminOnly: true },
    { to: "/settings", label: "Settings", icon: Gear },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Close drawer & search on route change
    useEffect(() => {
        setDrawerOpen(false);
        setSearchOpen(false);
        setQ("");
        setResults(null);
    }, [location.pathname]);

    // Lock body scroll when mobile drawer is open
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [drawerOpen]);

    const onLogout = async () => {
        await logout();
        navigate("/login");
    };

    const runSearch = async (val) => {
        setQ(val);
        if (!val) return setResults(null);
        try {
            const { data } = await api.get(`/search?q=${encodeURIComponent(val)}`);
            setResults(data);
        } catch {
            setResults(null);
        }
    };

    const navItems = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

    const SidebarBody = (
        <>
            <div className="px-5 lg:px-6 py-5 lg:py-6 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center btn-glow">
                        <Sparkle weight="fill" className="text-white" size={20} />
                    </div>
                    <div>
                        <div className="font-display text-base font-bold tracking-tight">
                            PROXIQ
                        </div>
                        <div className="text-[10px] tracking-[0.3em] text-slate-400 font-bold">
                            TECH AI
                        </div>
                    </div>
                </div>
                {/* Close button visible only inside the mobile drawer */}
                <button
                    onClick={() => setDrawerOpen(false)}
                    className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-slate-400"
                    aria-label="Close menu"
                    data-testid="drawer-close-btn"
                >
                    <X size={20} />
                </button>
            </div>
            <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
                {navItems.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={`nav-${n.label.toLowerCase()}`}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                                isActive
                                    ? "bg-gradient-to-r from-blue-500/20 to-blue-500/5 text-white border border-blue-500/30"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <n.icon size={18} weight="duotone" />
                        <span className="font-medium">{n.label}</span>
                    </NavLink>
                ))}
            </nav>
            <div className="px-3 py-4 border-t border-white/5">
                <div className="flex items-center gap-3 px-2 py-2">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 grid place-items-center font-display font-bold text-sm shrink-0">
                        {(user?.name || "A")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{user?.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                            {user?.role}
                        </div>
                    </div>
                    <button
                        onClick={onLogout}
                        data-testid="logout-btn"
                        className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400"
                        title="Logout"
                    >
                        <SignOut size={18} />
                    </button>
                </div>
            </div>
        </>
    );

    return (
        <div className="min-h-screen flex">
            {/* Desktop / large-tablet sidebar (lg+) */}
            <aside
                className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/5 bg-slate-950/70 backdrop-blur-xl"
                data-testid="sidebar"
            >
                {SidebarBody}
            </aside>

            {/* Mobile / tablet drawer */}
            {drawerOpen && (
                <button
                    type="button"
                    aria-label="Close menu overlay"
                    onClick={() => setDrawerOpen(false)}
                    className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm lg:hidden"
                    data-testid="drawer-overlay"
                />
            )}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col border-r border-white/10 bg-slate-950/95 backdrop-blur-xl shadow-2xl transition-transform duration-300 lg:hidden ${
                    drawerOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="mobile-drawer"
            >
                {SidebarBody}
            </aside>

            {/* Main column */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Topbar */}
                <header className="sticky top-0 z-30 border-b border-white/5 bg-slate-950/70 backdrop-blur-xl">
                    <div className="px-3 sm:px-4 lg:px-6 py-3 lg:py-4 flex items-center gap-2 sm:gap-3 lg:gap-4">
                        {/* Hamburger — mobile/tablet only */}
                        <button
                            onClick={() => setDrawerOpen(true)}
                            className="lg:hidden p-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200 shrink-0"
                            aria-label="Open menu"
                            data-testid="open-menu-btn"
                        >
                            <List size={20} weight="bold" />
                        </button>

                        {/* Compact brand on mobile only (in place of sidebar logo) */}
                        <div className="lg:hidden flex items-center gap-2 min-w-0">
                            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center shrink-0">
                                <Sparkle weight="fill" className="text-white" size={14} />
                            </div>
                            <div className="font-display text-sm font-bold tracking-tight truncate">
                                PROXIQ
                            </div>
                        </div>

                        <div className="relative flex-1 max-w-xl ml-auto lg:ml-0">
                            <MagnifyingGlass
                                size={16}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                            />
                            <input
                                data-testid="global-search-input"
                                value={q}
                                onChange={(e) => runSearch(e.target.value)}
                                onFocus={() => setSearchOpen(true)}
                                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                                placeholder="Search…"
                                className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2 sm:py-2.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/40 transition"
                            />
                            {searchOpen && results && (
                                <div
                                    className="absolute top-full left-0 right-0 mt-2 glass rounded-xl p-2 max-h-96 overflow-auto z-50"
                                    data-testid="global-search-results"
                                >
                                    {["orders", "students", "invoices", "payments"].map(
                                        (k) =>
                                            (results[k] || []).length > 0 && (
                                                <div key={k} className="p-2">
                                                    <div className="label-uppercase mb-2">
                                                        {k}
                                                    </div>
                                                    {results[k].map((it) => (
                                                        <div
                                                            key={it.id}
                                                            className="px-3 py-2 rounded-lg hover:bg-white/5 text-sm"
                                                        >
                                                            {it.client_name ||
                                                                it.name ||
                                                                it.party_name}{" "}
                                                            <span className="text-slate-500">
                                                                ·{" "}
                                                                {it.order_no ||
                                                                    it.enrolment_no ||
                                                                    it.invoice_no ||
                                                                    ""}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ),
                                    )}
                                    {Object.values(results).every(
                                        (arr) => !arr || arr.length === 0,
                                    ) && (
                                        <div className="px-3 py-4 text-sm text-slate-500">
                                            No results.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            className="relative p-2 sm:p-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 shrink-0"
                            data-testid="notifications-btn"
                            title="Notifications"
                            aria-label="Notifications"
                        >
                            <Bell size={18} weight="duotone" />
                            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500"></span>
                        </button>
                    </div>
                </header>

                <main className="flex-1 px-4 sm:px-5 lg:px-6 py-5 sm:py-6 lg:py-8 max-w-[1600px] w-full mx-auto">
                    {children}
                </main>

                <footer className="px-4 sm:px-6 py-4 sm:py-5 border-t border-white/5 text-center text-[11px] sm:text-xs text-slate-500">
                    <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
                        <span>
                            {settings.footer_signature || "Designed & Managed by Proxiq Tech AI"}
                        </span>
                        <span className="hidden sm:inline text-slate-700">·</span>
                        <span className="text-slate-400">
                            {settings.founder_name
                                ? `${settings.founder_name} — Founder of ${settings.company_name || "Proxiq Tech AI"}`
                                : "Aynul Memon — Founder of Proxiq Tech AI"}
                        </span>
                    </div>
                    <div className="mt-2 flex items-center justify-center gap-3 sm:gap-4 flex-wrap text-slate-500">
                        {settings.company_email && (
                            <a
                                href={`mailto:${settings.company_email}`}
                                className="inline-flex items-center gap-1.5 hover:text-slate-300 break-all"
                                data-testid="footer-email"
                            >
                                <EnvelopeSimple size={12} /> {settings.company_email}
                            </a>
                        )}
                        {settings.company_phone && (
                            <a
                                href={`tel:${settings.company_phone}`}
                                className="inline-flex items-center gap-1.5 hover:text-slate-300"
                                data-testid="footer-phone"
                            >
                                <Phone size={12} /> {settings.company_phone}
                            </a>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    );
}
